import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Flame, RefreshCw } from 'lucide-react';

const intensityColor = (val) => {
  if (val === 0) return 'bg-slate-100 text-slate-400';
  if (val < 0.2) return 'bg-cyan-50 text-cyan-600 border-cyan-200';
  if (val < 0.4) return 'bg-cyan-100 text-cyan-700 border-cyan-300';
  if (val < 0.6) return 'bg-amber-100 text-amber-700 border-amber-300';
  if (val < 0.8) return 'bg-orange-200 text-orange-800 border-orange-400';
  return 'bg-red-300 text-red-900 border-red-500 font-bold';
};

export default function BinsHeatmapPage() {
  const [data, setData] = useState({ cabinets: [], positions: [], max_count: 0, days: 30 });
  const [loading, setLoading] = useState(true);
  const [selectedCabinet, setSelectedCabinet] = useState('');
  const [days, setDays] = useState(30);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = { days };
      if (selectedCabinet) params.cabinet = selectedCabinet;
      const res = await api.get('/bins-heatmap', { params });
      setData(res.data);
    } catch { toast.error('Eroare la incarcarea heatmap'); }
    finally { setLoading(false); }
  }, [selectedCabinet, days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Group positions by cabinet -> shelf
  const grouped = {};
  data.positions.forEach(p => {
    const cab = p.cabinet || 'Necunoscut';
    if (!grouped[cab]) grouped[cab] = {};
    const shelf = p.shelf || '-';
    if (!grouped[cab][shelf]) grouped[cab][shelf] = [];
    grouped[cab][shelf].push(p);
  });
  // Sort shelves and positions
  Object.keys(grouped).forEach(cab => {
    Object.keys(grouped[cab]).forEach(shelf => {
      grouped[cab][shelf].sort((a, b) => (a.position || '').localeCompare(b.position || ''));
    });
  });

  const cabinets = data.cabinets.length > 0 ? data.cabinets : Object.keys(grouped).sort();
  const displayCabinets = selectedCabinet ? [selectedCabinet] : cabinets;

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <RefreshCw className="w-6 h-6 animate-spin text-orange-500" />
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3" data-testid="heatmap-title">
            <Flame className="w-7 h-7 text-orange-500" /> Heatmap Activitate Depozit
          </h1>
          <p className="text-sm text-slate-500 mt-1">Frecventa acces pozitii BIN in ultimele {days} zile (max: {data.max_count} miscari)</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={days} onChange={e => setDays(Number(e.target.value))} className="h-9 px-3 rounded-md border text-sm" data-testid="heatmap-days">
            <option value={7}>7 zile</option>
            <option value={14}>14 zile</option>
            <option value={30}>30 zile</option>
            <option value={90}>90 zile</option>
          </select>
          <select value={selectedCabinet} onChange={e => setSelectedCabinet(e.target.value)} className="h-9 px-3 rounded-md border text-sm" data-testid="heatmap-cabinet">
            <option value="">Toate dulapurile</option>
            {cabinets.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 text-xs text-slate-500">
        <span>Intensitate:</span>
        {[0, 0.1, 0.3, 0.5, 0.7, 0.9].map(v => (
          <span key={v} className={`px-2 py-0.5 rounded border ${intensityColor(v)}`}>
            {v === 0 ? 'Inactiv' : `${Math.round(v * 100)}%`}
          </span>
        ))}
      </div>

      {/* Cabinets */}
      {displayCabinets.map(cab => {
        const shelves = grouped[cab];
        if (!shelves) return null;
        const sortedShelves = Object.keys(shelves).sort().reverse();
        return (
          <div key={cab} className="bg-white rounded-xl border shadow-sm p-4" data-testid={`heatmap-cabinet-${cab}`}>
            <h3 className="text-sm font-semibold text-slate-700 mb-3">{cab}</h3>
            <div className="space-y-2">
              {sortedShelves.map(shelf => (
                <div key={shelf} className="flex items-center gap-2">
                  <span className="w-12 text-xs font-mono text-slate-400 shrink-0">{shelf}</span>
                  <div className="flex flex-wrap gap-1">
                    {shelves[shelf].map(pos => (
                      <div key={pos.id}
                        className={`w-10 h-10 rounded flex items-center justify-center text-[10px] border cursor-default transition-all hover:scale-110 ${intensityColor(pos.intensity)}`}
                        title={`${pos.code}: ${pos.activity_count} miscari`}
                        data-testid={`heatmap-pos-${pos.code}`}
                      >
                        {pos.position?.replace(/^P0?/, '') || pos.activity_count}
                      </div>
                    ))}
                  </div>
                  <span className="text-[10px] text-slate-400 ml-1">
                    {shelves[shelf].reduce((s, p) => s + p.activity_count, 0)} misc.
                  </span>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {data.positions.length === 0 && (
        <div className="text-center py-12 text-slate-400">Nicio pozitie BIN gasita</div>
      )}
    </div>
  );
}
